import type { ReactNode } from 'react';
import type { OnboardingProvider } from '@/types/scm-provider';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export type { OnboardingProvider } from '@/types/scm-provider';

interface OnboardingProviderSelectProps {
  onSelect: (provider: OnboardingProvider) => void;
}

const AzureDevOpsIcon = () => (
  <svg
    className="h-7 w-7"
    viewBox="0 0 24 24"
    aria-hidden
    focusable="false">
    <path
      fill="#0078D4"
      d="M0 8.877L2.247 5.91l8.405-3.416v.014L19.25 5.91v12.18l-8.598 3.416L0 17.926V8.877zm2.247 1.676v6.373l6.158 2.454V10.58L2.247 10.553zm8.405 8.827l6.158-2.454V10.58l-6.158 2.454v6.346zM11.652 2.494v5.09l6.158 2.454 2.247-2.967L11.652 2.494z"
    />
  </svg>
);

const GitHubIcon = () => (
  <svg
    className="h-7 w-7"
    viewBox="0 0 24 24"
    aria-hidden
    focusable="false">
    <path
      fill="currentColor"
      d="M12 0C5.37 0 0 5.37 0 12c0 5.3 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.085 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.418-1.305.762-1.605-2.665-.303-5.466-1.332-5.466-5.93 0-1.31.468-2.38 1.236-3.22-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23a11.5 11.5 0 0 1 3.003-.404c1.018.005 2.045.138 3.003.404 2.291-1.552 3.297-1.23 3.297-1.23.654 1.652.243 2.873.12 3.176.77.84 1.235 1.91 1.235 3.22 0 4.61-2.807 5.624-5.48 5.92.43.37.823 1.102.823 2.222 0 1.606-.015 2.898-.015 3.293 0 .322.216.694.825.576C20.565 21.795 24 17.295 24 12c0-6.63-5.37-12-12-12z"
    />
  </svg>
);

const PROVIDERS: Array<{
  id: OnboardingProvider;
  name: string;
  description: string;
  icon: ReactNode;
  iconClassName: string;
}> = [
  {
    id: 'azure-devops',
    name: 'Azure DevOps',
    description:
      'Connect repositories, pipelines, and release definitions from Azure DevOps.',
    icon: <AzureDevOpsIcon />,
    iconClassName:
      'border-[#c7e0f4] bg-[#eff6fc] text-[#0078D4]',
  },
  {
    id: 'github',
    name: 'GitHub',
    description:
      'Connect GitHub repositories, workflows, and automation pipelines.',
    icon: <GitHubIcon />,
    iconClassName: 'border-[#d0d7de] bg-[#f6f8fa] text-[#24292f]',
  },
];

const OnboardingProviderSelect = ({
  onSelect,
}: OnboardingProviderSelectProps) => (
  <div className="w-full max-w-[44rem]">
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
      {PROVIDERS.map(provider => (
        <Button
          key={provider.id}
          type="button"
          variant="ghost"
          className="group flex h-auto min-h-0 w-full flex-col items-stretch whitespace-normal border-0 bg-transparent p-0 text-left font-normal shadow-none hover:translate-y-0 hover:bg-transparent"
          onClick={() => onSelect(provider.id)}>
          <Card
            className={cn(
              'h-full w-full border-solid transition-all duration-200',
              'group-hover:-translate-y-0.5 group-hover:border-[var(--inv-accent)] group-hover:bg-[var(--inv-accent-soft-2)] group-hover:shadow-[var(--inv-shadow-hover,0_10px_24px_rgba(43,33,24,0.08))]',
              'group-focus-visible:-translate-y-0.5 group-focus-visible:border-[var(--inv-accent)] group-focus-visible:bg-[var(--inv-accent-soft-2)] group-focus-visible:shadow-[var(--inv-shadow-hover,0_10px_24px_rgba(43,33,24,0.08))] group-focus-visible:outline-none',
            )}>
            <CardContent className="flex flex-col items-center gap-3 px-5 py-7 text-center whitespace-normal">
              <span
                className={cn(
                  'flex h-14 w-14 items-center justify-center rounded-xl border border-solid',
                  provider.iconClassName,
                )}>
                {provider.icon}
              </span>
              <span className="text-base font-semibold text-[var(--inv-text)]">
                {provider.name}
              </span>
              <span className="text-[0.8125rem] font-normal leading-[1.45] text-[var(--inv-text-secondary)] whitespace-normal">
                {provider.description}
              </span>
            </CardContent>
          </Card>
        </Button>
      ))}
    </div>
  </div>
);

export default OnboardingProviderSelect;
